import { Component, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges , signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormControl, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { MatFormField, MatOption, MatSelect } from '@angular/material/select';
import { NgxMatSelectSearchModule } from 'ngx-mat-select-search';
import { Subject, takeUntil } from 'rxjs';
import { LanguageService } from 'src/app/services/account/language.service';
import { SettingsService } from 'src/app/services/account/settings.service';
import { AccountService } from 'src/app/services/account/account.service';
import { MapService } from 'src/app/services/map/map.service';
import { ECarType } from 'src/app/models/ecar-type';
import { ECar } from 'src/app/models/ecar';
import { VEHICLE_IMAGE_URL } from 'src/app/services/core/http.service';
import { VehicleImageFallbackDirective } from 'src/app/directives/vehicle-image-fallback.directive';
import { CarFilterPipe } from 'src/app/pipes/car-filter.pipe';
import { CarNamePipe } from 'src/app/pipes/car-name.pipe';
import { ValueIntegerPipe } from 'src/app/pipes/value-integer.pipe';
import { ConfirmationDialogComponent } from '../../confirmation-dialog/confirmation-dialog.component';
import { ModalOptionPanelComponent } from '../../modal-option-panel/modal-option-panel.component';
import { StackedModalsService } from 'src/app/services/modal/stacked-modals.service';

@Component({
  standalone: true,
  selector: 'app-vehicle-select-page',
  templateUrl: './vehicle-select-page.component.html',
  styleUrls: ['./vehicle-select-page.component.scss'],
  imports: [CommonModule, ReactiveFormsModule, MatFormField, MatSelect, MatOption, ModalOptionPanelComponent,
    NgxMatSelectSearchModule, CarFilterPipe, CarNamePipe, ValueIntegerPipe, VehicleImageFallbackDirective]
})
export class VehicleSelectPageComponent implements OnInit, OnChanges, OnDestroy {
  @Input() ECars: ECarType[] = [];
  @Input() CarTypes: any[] = [];
  @Input() selectedCar: ECarType | null = null;
  @Input() carType: string = '';
  @Input() carSubType: string = '';
  @Input() carTypeIndex: number = 0;
  @Input() multipleCarType: boolean = false;
  @Input() editedUserCar: ECar | null = null;
  @Input() customCarNameForm: FormGroup = new FormGroup({ customCarName: new FormControl() });

  @Output() carChanged = new EventEmitter<ECarType>();
  @Output() carTypeChanged = new EventEmitter<string>();
  @Output() carSubTypeChanged = new EventEmitter<string>();
  @Output() switchToVehicle = new EventEmitter<number | null>(); // userVehicleID or null = add new
  @Output() vehicleDeleted = new EventEmitter<number>();
  @Output() back = new EventEmitter<void>();

  public readonly userVehicles = signal<ECar[]>([]);

  readonly VEHICLE_IMAGE_URL = VEHICLE_IMAGE_URL;
  readonly ADD_NEW_KEY = '__add_new__';
  public readonly selectedDropdownValue = signal<string>('');

  public brandFilterCtrl = new FormControl('');
  public readonly filteredCarTypes = signal<any[]>([]);
  private destroyed = new Subject<void>();

  constructor(
    public languageService: LanguageService,
    public settingsService: SettingsService,
    public accountService: AccountService,
    public mapService: MapService,
    private stackedModalsService: StackedModalsService
  ) {
    this.customCarNameForm = this.customCarNameForm || new FormGroup({ customCarName: new FormControl() });
  }

  ngOnInit(): void {
    if (this.accountService.getIsAuthorized()) {
      this.accountService.ObservableUserVehicles.subscribe((list) => {
        if (list) {
          this.userVehicles.set(list);
          this.syncDropdownValue();
        }
      });
    }
    this.syncDropdownValue();
    this.filteredCarTypes.set(this.CarTypes);
    this.brandFilterCtrl.valueChanges.pipe(takeUntil(this.destroyed)).subscribe((search) => {
      this.filterCarTypes(search ?? '');
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['CarTypes']) {
      this.filterCarTypes(this.brandFilterCtrl.value ?? '');
    }
  }

  ngOnDestroy(): void {
    this.destroyed.next();
    this.destroyed.complete();
  }

  private filterCarTypes(search: string): void {
    const term = search.trim().toLowerCase();
    this.filteredCarTypes.set(term
      ? this.CarTypes.filter((type) => type.name.toLowerCase().includes(term))
      : this.CarTypes);
  }

  private syncDropdownValue(): void {
    if (this.editedUserCar) {
      this.selectedDropdownValue.set(String(this.editedUserCar.userVehicleID));
    } else {
      this.selectedDropdownValue.set(this.ADD_NEW_KEY);
    }
  }

  public dropdownChanged(value: string): void {
    if (value === this.ADD_NEW_KEY) {
      this.switchToVehicle.emit(null);
    } else {
      this.switchToVehicle.emit(Number(value));
    }
  }

  public getVehicleLabel(v: ECar): string {
    // For the vehicle currently being edited, reflect the in-progress custom name /
    // car selection so the dropdown updates live before the change is persisted.
    if (this.editedUserCar && v.userVehicleID === this.editedUserCar.userVehicleID) {
      const customName: string = this.customCarNameForm.get('customCarName')?.value;
      if (customName && customName.length > 0) return customName;
      return this.selectedCar?.Name || v.ECarType?.Name || '';
    }
    return v.name && v.name.length > 0 ? v.name : v.ECarType?.Name || '';
  }

  public deleteCar(): void {
    if (!this.editedUserCar) return;
    const vehicle = this.editedUserCar;
    const dialog = this.stackedModalsService.openModalWindow(ConfirmationDialogComponent, {
      data: {
        title: this.languageService.languageJSON.ManageCars_RemoveVehicle,
        message: this.languageService.languageJSON.ManageCars_RemoveVehicle_Description.replace(
          '$carname', `<b>${this.getVehicleLabel(vehicle)}?</b>`),
        warning: this.languageService.languageJSON.History_DeleteCarWarning,
        buttons: [this.languageService.languageJSON.Global_Delete, this.languageService.languageJSON.Global_Cancel]
      }, autoFocus: false
    });
    dialog.afterClosed().subscribe((resp) => {
      if (!resp) return;
      this.accountService.removeVehicle(vehicle.userVehicleID).subscribe(() => {
        this.vehicleDeleted.emit(vehicle.userVehicleID);
      });
    });
  }
}
